import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useBiometricWizard} from '../../../hooks/useBiometricWizard';
import {usePreRegisterConsultQuery} from '../../../queries/usePreRegisterConsultQuery';
import {useMediaQueries} from 'presentation/pages/private';
import {FacialBiometricEnum, HomeEnum, TelehealthBookingEnum} from 'presentation/constants';
import {useFacialBiometricsStore} from 'presentation/modules/booking/shared/store/useFacialBiometricsStore';
import {useGuardianStore} from '../../../store';
import {getUserInfoFromToken} from 'presentation/utils';

export function useInitialInstructions() {
	const {isTablet} = useMediaQueries();
	const navigate = useNavigate();
	const {search} = useLocation();
	const {setRegisterLater, hasFacialBiometrics, guardians} = useFacialBiometricsStore(
		state => state,
	);
	const location = useLocation();
	const {isUnder16, isTea} = useFacialBiometricsStore(state => state);
	const {selectedGuardianId, setSelectedGuardianId} = useGuardianStore();
	const {next} = useBiometricWizard();
	const {cdPessoa} = getUserInfoFromToken();
	const personCode = isTea && selectedGuardianId ? selectedGuardianId : Number(cdPessoa);
	const {refetch: refetchPreRegisterConsult} = usePreRegisterConsultQuery(personCode);
	const [isAlreadyPreRegistered, setIsAlreadyPreRegistered] = useState(false);
	const [isCheckingPreRegister, setIsCheckingPreRegister] = useState(false);
	const [showGuardianSelection, setShowGuardianSelection] = useState(isTea);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const isFromTelehealthBooking = location.state?.isTelehealth === true || search;

	async function handleNext() {
		setIsCheckingPreRegister(true);
		try {
			const {data: preRegisterConsultData} = await refetchPreRegisterConsult({
				throwOnError: true,
			});

			const hasPreRegister = Boolean(preRegisterConsultData?.preCadastro);

			setIsAlreadyPreRegistered(hasPreRegister);

			if (hasPreRegister) return;

			next();
		} catch {
			setIsAlreadyPreRegistered(false);
			setShowErrorModal(true);
		} finally {
			setIsCheckingPreRegister(false);
		}
	}

	function handleErrorModalClose() {
		setShowErrorModal(false);
		navigate(HomeEnum.HOME_PAGE);
	}

	function handleBackToHome() {
		if (search) {
			setRegisterLater(true);
			navigate(
				{pathname: TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW, search},
				{state: {from: FacialBiometricEnum.FACIAL_BIOMETRIC}},
			);
			return;
		}
		navigate(HomeEnum.HOME_PAGE);
	}

	function handleSelectGuardian(guardian: {personId: number}) {
		setSelectedGuardianId(guardian.personId);
		setShowGuardianSelection(false);
	}

	const canRegister =
		isTea && guardians.length > 0 && guardians.some(guardian => !guardian.facialRegistered);

	return {
		isTablet,
		isAlreadyPreRegistered,
		isCheckingPreRegister,
		showGuardianSelection,
		showErrorModal,
		isFromTelehealthBooking,
		setShowErrorModal,
		handleNext,
		handleErrorModalClose,
		handleBackToHome,
		handleSelectGuardian,
		canRegister,
		hasFacialBiometrics,
		isUnder16,
		isTea,
	};
}
